/**
 * Redirect target sanitising.
 *
 * Shared by both sides of the sign-in flows on purpose: the Worker signs the
 * target into the OAuth `state`, and the client navigates to a target read from
 * a query parameter after the header-less ticket exchange. A second, weaker
 * implementation on the client is what turns a same-origin check into an open
 * redirect, so the rule lives in exactly one place.
 *
 * Only same-origin relative paths survive. Everything below falls back:
 *
 * - absolute URLs and protocol relative paths (`//host`);
 * - backslashes, which the URL parser turns into slashes for http/https, so
 *   `/\host` would otherwise become `//host`;
 * - ASCII tab, LF and CR, which the parser strips before parsing, so `/<TAB>/host`
 *   would likewise become `//host`;
 * - any other control character, and any path that normalises into `//host`
 *   (for example `/..//host`).
 */

/** Base a relative target is resolved against; only paths of this origin pass. */
const SAME_ORIGIN_BASE = 'https://redirect.invalid'

/** Characters the URL parser rewrites or strips, plus every other control char. */
const UNSAFE_PATH = /[\\\u0000-\u001f\u007f]/

/**
 * Returns a same-origin path, or `fallback` when the value cannot be one.
 *
 * The result is the parser-normalised path, so no caller can be handed a value
 * that a browser would resolve differently from the check performed here.
 */
export function sanitizeRedirectPath(value: string | null | undefined, fallback = '/me'): string {
  const candidate = value?.trim()
  if (
    !candidate ||
    !candidate.startsWith('/') ||
    candidate.startsWith('//') ||
    UNSAFE_PATH.test(candidate)
  ) {
    return fallback
  }

  let url: URL
  try {
    url = new URL(candidate, SAME_ORIGIN_BASE)
  } catch {
    return fallback
  }

  if (url.origin !== SAME_ORIGIN_BASE || url.pathname.startsWith('//')) {
    return fallback
  }

  return `${url.pathname}${url.search}${url.hash}`
}
